import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import {
    Description,
    Dialog,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    Heading,
    LoadingPanel,
} from '@gitbook/spine-react';

import { UpgradeDialogContext, type UpgradeDialogState } from './UpgradeDialogContext';

const UpgradeDialog = React.lazy(() =>
    import('./UpgradeDialog').then((m) => ({ default: m.UpgradeDialog }))
);

/**
 * Provide a locally mounted upgrade dialog for descendant hooks and command handlers.
 */
export function UpgradeDialogProvider(props: { children: React.ReactNode }): React.ReactElement {
    const { children } = props;
    const [state, setState] = React.useState<UpgradeDialogState | null>(null);
    const [aiRestrictedOpen, setAIRestrictedOpen] = React.useState(false);

    const openUpgradeDialog = React.useCallback((initialState: UpgradeDialogState) => {
        analytics.logEvent(
            analytics.createOrgEvent(initialState.organization.id, {
                type: 'organization_open_upgrade',
                currentFeaturesSet: initialState.organization.plan,
                product: initialState.targetProduct,
                reason: initialState.reason,
                ...initialState.analyticsContext,
            })
        );
        setState(initialState);
    }, []);

    const openAIRestrictedDialog = React.useCallback(() => setAIRestrictedOpen(true), []);

    const value = React.useMemo(
        () => ({ openUpgradeDialog, openAIRestrictedDialog }),
        [openUpgradeDialog, openAIRestrictedDialog]
    );

    return (
        <UpgradeDialogContext.Provider value={value}>
            {children}
            <DialogRoot
                isOpen={Boolean(state)}
                onOpenChange={(open: boolean) => {
                    if (!open) {
                        setState(null);
                    }
                }}
            >
                {state ? (
                    <React.Suspense
                        fallback={
                            <Dialog>
                                <LoadingPanel />
                            </Dialog>
                        }
                    >
                        <UpgradeDialog
                            organization={state.organization}
                            targetProduct={state.targetProduct}
                            reason={state.reason}
                            initialState={state.initialState}
                            onSuccess={state.onSuccess}
                        />
                    </React.Suspense>
                ) : null}
            </DialogRoot>
            <DialogRoot isOpen={aiRestrictedOpen} onOpenChange={setAIRestrictedOpen}>
                {aiRestrictedOpen ? (
                    <Dialog>
                        <DialogHeader>
                            <Heading>Feature unavailable</Heading>
                            <Description>
                                This feature isn't available for this organization.
                            </Description>
                        </DialogHeader>
                        <DialogFooter>
                            <DialogCloseButton />
                        </DialogFooter>
                    </Dialog>
                ) : null}
            </DialogRoot>
        </UpgradeDialogContext.Provider>
    );
}
